import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@istari-frontend/ui";
import { Archive, ArchiveRestore, Circle, EllipsisVertical, Pencil, Plus, SearchIcon, Settings2 } from "lucide-react";
import { useId } from "react";

import type {
  Conversation,
  ConversationGroupBy,
  ConversationStatus,
  ConversationStatusFilter,
} from "~/ai-chat/model/conversation";
import { groupConversationsByRecency, groupConversationsByStatus } from "~/ai-chat/model/conversation-grouping";
import { useInlineTitleEdit } from "~/ai-chat/ui/use-inline-title-edit";
import { cn } from "~/utils/cn";

interface ConversationListProps {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onRename: (id: string, title: string | null) => void;
  onArchive: (id: string, archived: boolean) => void;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: ConversationStatusFilter;
  onStatusFilterChange: (value: ConversationStatusFilter) => void;
  groupBy: ConversationGroupBy;
  onGroupByChange: (value: ConversationGroupBy) => void;
  onLoadMore: () => void;
  hasMore: boolean;
  isFetchingMore: boolean;
  isLoading?: boolean;
  // Conversations streaming right now (live), so their dot shows amber immediately
  // instead of waiting on the list refetch.
  runningIds: Set<string>;
}

export function ConversationList({
  conversations,
  activeId,
  onSelect,
  onNew,
  onRename,
  onArchive,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  groupBy,
  onGroupByChange,
  onLoadMore,
  hasMore,
  isFetchingMore,
  isLoading,
  runningIds,
}: ConversationListProps) {
  const headingId = useId();
  const searchId = useId();
  const groups =
    groupBy === "status"
      ? groupConversationsByStatus(conversations, runningIds)
      : groupBy === "none"
        ? [{ label: "", conversations }]
        : groupConversationsByRecency(conversations, new Date());

  return (
    <nav aria-labelledby={headingId} className="bg-sidebar flex h-full flex-col">
      <h2 id={headingId} className="sr-only">
        Conversations
      </h2>
      <div className="border-sidebar-border flex shrink-0 items-center gap-3 border-b px-3.5 py-2">
        <div className="border-input bg-background focus-within:border-ring focus-within:ring-ring/50 flex h-8 min-w-0 flex-1 items-center gap-2 rounded-[10px] border px-2.5 shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]">
          <SearchIcon className="text-muted-foreground size-4 shrink-0" />
          <input
            id={searchId}
            type="search"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search conversations…"
            aria-label="Search conversations"
            className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={onNew} className="shrink-0 gap-1.5 rounded-[10px]">
          <Plus className="size-4" />
          New
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="secondary"
              size="icon-sm"
              aria-label="Filter and group conversations"
              className="shrink-0 rounded-[10px]"
            >
              <Settings2 className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Status</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={statusFilter}
              onValueChange={(value) => onStatusFilterChange(value as ConversationStatusFilter)}
            >
              <DropdownMenuRadioItem value="active">Active</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="all">All</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Group by</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={groupBy}
              onValueChange={(value) => onGroupByChange(value as ConversationGroupBy)}
            >
              <DropdownMenuRadioItem value="date">Date</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="status">Status</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="none">None</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground px-4 py-3 text-sm">Loading conversations…</p>
      ) : conversations.length === 0 ? (
        <p className="text-muted-foreground px-4 py-3 text-sm">
          {search.trim().length > 0 ? "No matching conversations." : "No conversations yet."}
        </p>
      ) : (
        <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
          {groups.map((group) => (
            <section key={group.label} className="flex flex-col gap-0.5">
              {group.label ? (
                <h3 className="text-muted-foreground px-2 py-1.5 text-xs tracking-wide">{group.label}</h3>
              ) : null}
              <ul className="flex flex-col gap-0.5">
                {group.conversations.map((conversation) => (
                  <ConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    isActive={conversation.id === activeId}
                    isRunning={runningIds.has(conversation.id)}
                    onSelect={onSelect}
                    onRename={onRename}
                    onArchive={onArchive}
                  />
                ))}
              </ul>
            </section>
          ))}
          {hasMore ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onLoadMore}
              disabled={isFetchingMore}
              className="w-full"
            >
              {isFetchingMore ? "Loading…" : "Load more"}
            </Button>
          ) : null}
        </div>
      )}
    </nav>
  );
}

const UNTITLED_LABEL = "Untitled chat";

interface ConversationRowProps {
  conversation: Conversation;
  isActive: boolean;
  isRunning: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string | null) => void;
  onArchive: (id: string, archived: boolean) => void;
}

// Status dot, matching the chat tabs: amber = processing, green = a completed turn,
// red = a terminal error, neutral gray for aborted / idle.
function statusDot(status: ConversationStatus | null): { className: string; label: string } | null {
  switch (status) {
    case "running":
      return { className: "fill-warning text-warning", label: "Processing" };
    case "completed":
      return { className: "fill-success text-success", label: "Completed" };
    case "error":
      return { className: "fill-destructive text-destructive", label: "Error" };
    default:
      return null;
  }
}

function ConversationRow({ conversation, isActive, isRunning, onSelect, onRename, onArchive }: ConversationRowProps) {
  const edit = useInlineTitleEdit(conversation.title, (title) => onRename(conversation.id, title));
  const label = conversation.title ?? UNTITLED_LABEL;
  const isArchived = conversation.archivedAt !== null;
  const dot = statusDot(isRunning ? "running" : conversation.status);

  if (edit.isEditing) {
    return (
      <li>
        <input
          aria-label={`Rename ${label}`}
          autoFocus
          value={edit.draft ?? ""}
          onChange={(event) => edit.setDraft(event.target.value)}
          onBlur={edit.handleBlur}
          onKeyDown={edit.handleKeyDown}
          className={cn(
            "text-foreground w-full rounded-lg bg-transparent px-3 py-1.5 text-sm outline-none",
            "focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          )}
        />
      </li>
    );
  }

  return (
    <li className={cn("group/row relative rounded-lg", isActive && "bg-sidebar-accent")}>
      <button
        type="button"
        aria-current={isActive ? "true" : undefined}
        // aria-label overrides descendant text, so fold the status into the accessible
        // name — the dot is visible but decorative, and the id snippet is just noise for SR.
        aria-label={dot ? `${label}, ${dot.label}` : label}
        onClick={() => onSelect(conversation.id)}
        className={cn(
          "flex w-full items-center gap-1.5 rounded-lg px-3 py-1.5 pr-9 text-left transition-colors outline-none",
          "hover:bg-sidebar-accent focus-visible:ring-ring/50 focus-visible:ring-[3px]",
          isActive && "font-medium",
        )}
      >
        <Circle
          className={cn("size-3 shrink-0", dot ? dot.className : "fill-muted text-muted-foreground")}
          aria-hidden="true"
        />
        <span className="text-foreground min-w-0 flex-1 truncate text-sm">{label}</span>
        <span className="text-muted-foreground shrink-0 font-mono text-xs">{conversation.id.slice(0, 6)}…</span>
      </button>
      <div
        className={cn(
          "absolute top-1/2 right-1 -translate-y-1/2 transition-opacity",
          !isActive && "opacity-0 group-focus-within/row:opacity-100 group-hover/row:opacity-100",
        )}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Options for ${label}`}
              className="hover:bg-accent flex items-center justify-center rounded p-1"
            >
              <EllipsisVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={edit.beginEdit}>
              <Pencil />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onArchive(conversation.id, !isArchived)}>
              {isArchived ? <ArchiveRestore /> : <Archive />}
              {isArchived ? "Unarchive" : "Archive"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}
